/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import {
  ProfileManagementServiceService,
  type ProfileListResponse,
  type ProfileResponse,
  type PartnerConfigResponse,
  type SelectedProfileResponse,
  type TProfileManagementServiceCreateProfileParams,
  type TProfileManagementServiceDeleteProfileParams,
  type TProfileManagementServiceFetchAllProfilesParams,
  type TProfileManagementServiceFetchProfileParams,
  type TProfileManagementServiceSetSelectedProfileParams,
  type TProfileManagementServiceUpdateProfileParams,
  type TProfileManagementServiceFetchPartnerConfigParams,
  type TServicesParams
} from "@tivocorp/data-domain-lib";

import type { IProfileManagementInteractor } from "../../interfaces";
import type { TNetworkOptions } from "../../types";
import { AppStartupInteractor } from "../preSignin";
import { Log } from "../../logging/Log";
import { ApplicationFeatureArea } from "../../enums";
import { TIVO_SERVICES } from "../../../config";
import { BaseInteractor, ServiceLocator } from "../base";
import { CommonHeaders } from "../../CommonHeaders";
import { TokenManager } from "../../auth/TokenManager";
import type { TAuthContext } from "../../auth/TokenManager";
import { ProfileManagementServiceError } from "../../error";

function getErrorProperty(value: unknown, property: string): unknown {
  if (value === null || typeof value !== "object" || !(property in value)) {
    return undefined;
  }
  return (value as Record<string, unknown>)[property];
}

function resolveErrorMessage(error: unknown, responseData: unknown): string {
  const responseMessage: unknown = getErrorProperty(responseData, "message");
  if (typeof responseMessage === "string") {
    return responseMessage;
  }
  if (error instanceof Error) {
    return error.message;
  }
  const errorMessage: unknown = getErrorProperty(error, "message");
  return typeof errorMessage === "string"
    ? errorMessage
    : "Profile operation failed";
}

/**
 * Shared, atomic interactor wrapping {@link ProfileManagementServiceService}.
 *
 * Keeps profile CRUD logic behind one app-level seam so views and Redux
 * thunks can stay simple and testable.
 */
export class ProfileManagementInteractor
  extends BaseInteractor
  implements IProfileManagementInteractor
{
  private static readonly TAG: string = "ProfileManagementInteractor";

  public constructor(
    private readonly mFeatureArea: ApplicationFeatureArea = ApplicationFeatureArea.ALL
  ) {
    super();
  }

  private static isCancellationError(error: unknown): boolean {
    if (error === null || typeof error !== "object") {
      return false;
    }
    const name: string = "name" in error ? String(error.name) : "";
    const code: string = "code" in error ? String(error.code) : "";
    return (
      name === "AbortError" ||
      name === "CanceledError" ||
      code === "ERR_CANCELED"
    );
  }

  private static isCallerSignalAborted(
    callerSignal: AbortSignal | undefined
  ): boolean {
    if (callerSignal === undefined) {
      return false;
    }
    return callerSignal.aborted;
  }

  private static buildServiceError(
    error: unknown
  ): ProfileManagementServiceError {
    const response: unknown = getErrorProperty(error, "response");
    const responseData: unknown = getErrorProperty(response, "data");
    const statusValue: unknown = getErrorProperty(response, "status");
    const status: number | undefined =
      typeof statusValue === "number" ? statusValue : undefined;
    const serverCodeValue: unknown = getErrorProperty(responseData, "code");
    const serverCode: string =
      typeof serverCodeValue === "string" ? serverCodeValue : "";
    const clientCodeValue: unknown = getErrorProperty(error, "code");
    const clientCode: string =
      typeof clientCodeValue === "string" ? clientCodeValue : "";
    const message: string = resolveErrorMessage(error, responseData);

    let code: string = serverCode !== "" ? serverCode : clientCode;
    if (status === 401) {
      code = "AUTHENTICATION_REQUIRED";
    } else if (status === 403) {
      code = "ACCESS_DENIED";
    } else if (status === 404) {
      code = "RESOURCE_NOT_FOUND";
    } else if (message === "Network unavailable") {
      code = "ERR_NETWORK";
    }

    const serviceError = new ProfileManagementServiceError(code, message);
    serviceError.setDescription(message);
    return serviceError;
  }

  private static createInvalidResponseError(
    operationName: string
  ): ProfileManagementServiceError {
    return new ProfileManagementServiceError(
      "ERR_BAD_RESPONSE",
      operationName + " returned an invalid response"
    );
  }

  private static isNonEmptyString(value: unknown): boolean {
    return typeof value === "string" && value.length > 0;
  }

  private static isValidAvatar(avatar: unknown): boolean {
    return (
      avatar !== null &&
      typeof avatar === "object" &&
      typeof getErrorProperty(avatar, "avatarId") === "string"
    );
  }

  private static hasValidOptionalProperty(
    value: unknown,
    property: string,
    expectedType: "boolean" | "string"
  ): boolean {
    const propertyValue: unknown = getErrorProperty(value, property);
    return propertyValue === undefined || typeof propertyValue === expectedType;
  }

  private static isValidProfile(profile: unknown): boolean {
    const attributes: unknown = getErrorProperty(profile, "attributes");
    const avatar: unknown = getErrorProperty(attributes, "avatar");
    const hasValidAvatar: boolean =
      avatar === undefined || ProfileManagementInteractor.isValidAvatar(avatar);

    return (
      profile !== null &&
      typeof profile === "object" &&
      ProfileManagementInteractor.isNonEmptyString(
        getErrorProperty(profile, "profileId")
      ) &&
      attributes !== null &&
      typeof attributes === "object" &&
      typeof getErrorProperty(attributes, "name") === "string" &&
      hasValidAvatar &&
      ProfileManagementInteractor.hasValidOptionalProperty(
        profile,
        "isAdmin",
        "boolean"
      ) &&
      ProfileManagementInteractor.hasValidOptionalProperty(
        profile,
        "isSelected",
        "boolean"
      ) &&
      ProfileManagementInteractor.hasValidOptionalProperty(
        profile,
        "createdAt",
        "string"
      ) &&
      ProfileManagementInteractor.hasValidOptionalProperty(
        profile,
        "updatedAt",
        "string"
      )
    );
  }

  private static validateProfilesResponse(response: ProfileListResponse): void {
    const profiles: unknown = getErrorProperty(response, "profiles");
    if (
      !Array.isArray(profiles) ||
      !profiles.every(ProfileManagementInteractor.isValidProfile)
    ) {
      throw ProfileManagementInteractor.createInvalidResponseError(
        "fetchAllProfiles()"
      );
    }
  }

  private static validateProfileResponse(
    response: ProfileResponse,
    operationName: string
  ): void {
    if (!ProfileManagementInteractor.isValidProfile(response)) {
      throw ProfileManagementInteractor.createInvalidResponseError(
        operationName
      );
    }
  }

  private static validatePartnerConfigResponse(
    response: PartnerConfigResponse
  ): void {
    const attributes: unknown = getErrorProperty(response, "attributes");
    const defaultAvatar: unknown = getErrorProperty(
      attributes,
      "defaultAvatar"
    );
    const allowedAvatars: unknown = getErrorProperty(
      attributes,
      "allowedAvatars"
    );
    const maxProfiles: unknown = getErrorProperty(attributes, "maxProfiles");
    const hasValidMaxProfiles: boolean =
      maxProfiles === undefined ||
      (typeof maxProfiles === "number" &&
        Number.isInteger(maxProfiles) &&
        maxProfiles >= 0);

    if (
      !ProfileManagementInteractor.isNonEmptyString(
        getErrorProperty(response, "partnerId")
      ) ||
      attributes === null ||
      typeof attributes !== "object" ||
      !ProfileManagementInteractor.isValidAvatar(defaultAvatar) ||
      !Array.isArray(allowedAvatars) ||
      !allowedAvatars.every(ProfileManagementInteractor.isValidAvatar) ||
      !hasValidMaxProfiles
    ) {
      throw ProfileManagementInteractor.createInvalidResponseError(
        "fetchPartnerConfig()"
      );
    }
  }

  private static validateSelectedProfileResponse(
    response: SelectedProfileResponse
  ): void {
    if (
      !ProfileManagementInteractor.isNonEmptyString(
        getErrorProperty(response, "profileId")
      ) ||
      !ProfileManagementInteractor.isNonEmptyString(
        getErrorProperty(response, "updatedAt")
      )
    ) {
      throw ProfileManagementInteractor.createInvalidResponseError(
        "setSelectedProfile()"
      );
    }
  }

  private async executeProfileOperation<T>(
    methodName: string,
    operation: (signal: AbortSignal) => Promise<T>,
    options?: TNetworkOptions
  ): Promise<T> {
    try {
      return await this.executeGuarded(methodName, operation, options);
    } catch (error: unknown) {
      const callerSignal: AbortSignal | undefined =
        options !== undefined ? options.signal : undefined;
      if (
        ProfileManagementInteractor.isCallerSignalAborted(callerSignal) ||
        ProfileManagementInteractor.isCancellationError(error) ||
        error instanceof ProfileManagementServiceError
      ) {
        throw error;
      }
      throw ProfileManagementInteractor.buildServiceError(error);
    }
  }

  private async buildProfileManagementServicesParams(): Promise<TServicesParams> {
    const common = new CommonHeaders(this.mFeatureArea);
    const authContext: TAuthContext = await TokenManager.getAuthContext();
    return common.toServicesParams({
      basePath:
        await new AppStartupInteractor().getServiceEndpointByServiceName(
          TIVO_SERVICES.ProfileManagementService
        ),
      accessToken: authContext.accessToken,
      profileId:
        authContext.profileId === null ? undefined : authContext.profileId
    });
  }

  public async fetchAllProfiles(
    params: TProfileManagementServiceFetchAllProfilesParams,
    options?: TNetworkOptions
  ): Promise<ProfileListResponse> {
    return this.executeProfileOperation(
      "fetchAllProfiles",
      async (guardedSignal: AbortSignal): Promise<ProfileListResponse> => {
        Log.d(ProfileManagementInteractor.TAG, "fetchAllProfiles() called");
        const service: ProfileManagementServiceService =
          ServiceLocator.getInstance(
            ProfileManagementServiceService,
            await this.buildProfileManagementServicesParams()
          );
        const response: ProfileListResponse = await service.fetchAllProfiles(
          params,
          guardedSignal
        );
        ProfileManagementInteractor.validateProfilesResponse(response);
        return response;
      },
      options
    );
  }

  public async createProfile(
    params: TProfileManagementServiceCreateProfileParams,
    options?: TNetworkOptions
  ): Promise<ProfileResponse> {
    return this.executeProfileOperation(
      "createProfile",
      async (guardedSignal: AbortSignal): Promise<ProfileResponse> => {
        Log.d(ProfileManagementInteractor.TAG, "createProfile() called");
        const service: ProfileManagementServiceService =
          ServiceLocator.getInstance(
            ProfileManagementServiceService,
            await this.buildProfileManagementServicesParams()
          );
        const response: ProfileResponse = await service.createProfile(
          params,
          guardedSignal
        );
        ProfileManagementInteractor.validateProfileResponse(
          response,
          "createProfile()"
        );
        return response;
      },
      options
    );
  }

  public async fetchProfile(
    params: TProfileManagementServiceFetchProfileParams,
    options?: TNetworkOptions
  ): Promise<ProfileResponse> {
    return this.executeProfileOperation(
      "fetchProfile",
      async (guardedSignal: AbortSignal): Promise<ProfileResponse> => {
        Log.d(ProfileManagementInteractor.TAG, "fetchProfile() called");
        const service: ProfileManagementServiceService =
          ServiceLocator.getInstance(
            ProfileManagementServiceService,
            await this.buildProfileManagementServicesParams()
          );
        const response: ProfileResponse = await service.fetchProfile(
          params,
          guardedSignal
        );
        ProfileManagementInteractor.validateProfileResponse(
          response,
          "fetchProfile()"
        );
        return response;
      },
      options
    );
  }

  public async updateProfile(
    params: TProfileManagementServiceUpdateProfileParams,
    options?: TNetworkOptions
  ): Promise<ProfileResponse> {
    return this.executeProfileOperation(
      "updateProfile",
      async (guardedSignal: AbortSignal): Promise<ProfileResponse> => {
        Log.d(ProfileManagementInteractor.TAG, "updateProfile() called");
        const service: ProfileManagementServiceService =
          ServiceLocator.getInstance(
            ProfileManagementServiceService,
            await this.buildProfileManagementServicesParams()
          );
        const response: ProfileResponse = await service.updateProfile(
          params,
          guardedSignal
        );
        ProfileManagementInteractor.validateProfileResponse(
          response,
          "updateProfile()"
        );
        return response;
      },
      options
    );
  }

  public async deleteProfile(
    params: TProfileManagementServiceDeleteProfileParams,
    options?: TNetworkOptions
  ): Promise<void> {
    return this.executeProfileOperation(
      "deleteProfile",
      async (guardedSignal: AbortSignal): Promise<void> => {
        Log.d(ProfileManagementInteractor.TAG, "deleteProfile() called");
        const service: ProfileManagementServiceService =
          ServiceLocator.getInstance(
            ProfileManagementServiceService,
            await this.buildProfileManagementServicesParams()
          );
        return service.deleteProfile(params, guardedSignal);
      },
      options
    );
  }

  public async fetchPartnerConfig(
    params: TProfileManagementServiceFetchPartnerConfigParams,
    options?: TNetworkOptions
  ): Promise<PartnerConfigResponse> {
    return this.executeProfileOperation(
      "fetchPartnerConfig",
      async (guardedSignal: AbortSignal): Promise<PartnerConfigResponse> => {
        Log.d(ProfileManagementInteractor.TAG, "fetchPartnerConfig() called");
        const service: ProfileManagementServiceService =
          ServiceLocator.getInstance(
            ProfileManagementServiceService,
            await this.buildProfileManagementServicesParams()
          );
        const response: PartnerConfigResponse =
          await service.fetchPartnerConfig(params, guardedSignal);
        ProfileManagementInteractor.validatePartnerConfigResponse(response);
        return response;
      },
      options
    );
  }

  public async setSelectedProfile(
    params: TProfileManagementServiceSetSelectedProfileParams,
    options?: TNetworkOptions
  ): Promise<SelectedProfileResponse> {
    return this.executeProfileOperation(
      "setSelectedProfile",
      async (guardedSignal: AbortSignal): Promise<SelectedProfileResponse> => {
        Log.d(ProfileManagementInteractor.TAG, "setSelectedProfile() called");
        const service: ProfileManagementServiceService =
          ServiceLocator.getInstance(
            ProfileManagementServiceService,
            await this.buildProfileManagementServicesParams()
          );
        const response: SelectedProfileResponse =
          await service.setSelectedProfile(params, guardedSignal);
        ProfileManagementInteractor.validateSelectedProfileResponse(response);
        return response;
      },
      options
    );
  }
}
